import {useCallback, useEffect, useRef} from 'react';

import {useCurrency} from '@salesforce/retail-react-app/app/hooks';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';

import {collectGa4BasketProductIds} from '@amblique/king-living/app/analytics/ga4/basket-ga4';
import {fetchGa4OptionProductMeta} from '@amblique/king-living/app/analytics/ga4/customize-atc';
import {pushEvent} from '@amblique/king-living/app/analytics/ga4/data-layer';
import {
  getCustomerParamsWithEmail,
  getSignUpCustomerParamsWithEmail,
} from '@amblique/king-living/app/analytics/ga4/customer-context';
import {roundGa4Currency} from '@amblique/king-living/app/analytics/ga4/currency';
import {resolveGa4ItemId, applyGa4CartItemCategories, toGa4AddToCartItem, toGa4Item} from '@amblique/king-living/app/analytics/ga4/item-mapper';
import {buildGa4BasketEventItemsFromBasket} from '@amblique/king-living/app/analytics/ga4/view-cart';
import {
  buildSessionKey,
  clearSession,
  readSession,
  writeSession,
} from '@amblique/king-living/app/utils/session-utils';
import {hashSha256Hex, isGtmEnabled} from '@amblique/king-living/app/utils/gtm-utils';

const listMeta = (name) => ({id: name, name});

const toGa4ItemArray = (items) => (Array.isArray(items) ? items : []);

const pushCustomerGa4Event = async (eventName, customer, payload) => pushEvent(eventName, {
  ...(await getCustomerParamsWithEmail(customer)),
  ...payload,
});

/**
 * GT-011 — server-side forwarding for `purchase` (confirmation → PWA server → sGTM).
 * Does not push to client `dataLayer`; unlike funnel events which use `pushEvent`.
 * @param {string} eventName
 * @param {object} payload
 * @param {{ siteId?: string }} [options]
 */
export const pushServerSideEvent = async (eventName, payload = {}, {siteId} = {}) => {
  if (typeof window === 'undefined' || !isGtmEnabled()) {
    return false;
  }

  const event_id = typeof crypto?.randomUUID === 'function' ?
    (await hashSha256Hex(crypto.randomUUID()) || crypto.randomUUID()) :
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const endpoint = siteId ?
    `/server/analytics/track?siteId=${encodeURIComponent(siteId)}` :
    '/server/analytics/track';

  try {
    const response = await fetch(endpoint, {
      body: JSON.stringify({
        event: eventName,
        ...payload,
        event_id,
      }),
      headers: {'Content-Type': 'application/json'},
      method: 'POST',
    });

    if (!response.ok) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[GA4] server-side event failed', response.status);
      }

      return false;
    }

    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[GA4] server-side event error', error);
    }

    return false;
  }
};

export const VIEW_ITEM_LIST = {
  PLP: listMeta('PLP'),
  FULL_SEARCH: listMeta('Full Search Results'),
  PRODUCT_CAROUSEL: listMeta('Product Carousel'),
  QUICK_SEARCH: listMeta('Quick Search'),
};

export const resolveGa4ListMeta = (title) => {
  const name = String(title || '').trim() || VIEW_ITEM_LIST.PRODUCT_CAROUSEL.name;

  return {id: name, name};
};

export const trackSelectItem = async ({
  listMeta: list,
  product,
  index,
  currency,
  customer,
}) => {
  if (!list?.id || !product || typeof index !== 'number') {
    return false;
  }

  const item = toGa4Item(product, {currency, index, listItem: true});

  if (!item) {
    return false;
  }

  return pushCustomerGa4Event('select_item', customer, {
    ecommerce: {
      item_list_id: list.id,
      item_list_name: list.name ?? list.id,
      items: [item],
    },
  });
};

export const useTrackSelectItem = () => {
  const {data: customer} = useCurrentCustomer();
  const {currency} = useCurrency();

  return useCallback(
    (params) => trackSelectItem({...params, currency, customer}),
    [currency, customer]
  );
};

export const useGa4TileSelect = (listMeta, afterSelect) => {
  const trackSelectItem = useTrackSelectItem();

  return useCallback(
    (product, index) => {
      void trackSelectItem({index, listMeta, product});
      afterSelect?.(product, index);
    },
    [afterSelect, listMeta, trackSelectItem]
  );
};

export const createViewItemListTracker = () => {
  const firedIds = new Set();

  return {
    filterUnfired(products) {
      return toGa4ItemArray(products).filter((product) => {
        const itemId = resolveGa4ItemId(product);

        return itemId && !firedIds.has(itemId);
      });
    },
    markFired(products) {
      toGa4ItemArray(products).forEach((product) => {
        const itemId = resolveGa4ItemId(product);

        if (itemId) {
          firedIds.add(itemId);
        }
      });
    },
    reset() {
      firedIds.clear();
    },
  };
};

export const trackLogin = () => pushEvent('login', {ecommerce: {method: 'Website'}});

export const trackSignUp = async ({method, customer}) => pushEvent('sign_up', {
  ...(await getSignUpCustomerParamsWithEmail(customer)),
  ecommerce: {method},
});

export const trackSearch = async ({searchTerm, customer}) => {
  const term = String(searchTerm || '').trim();

  if (!term) {
    return false;
  }

  return pushCustomerGa4Event('search', customer, {
    ecommerce: {search_term: term},
  });
};

/** Σ (`price` × `quantity`) on reported `items[]` — `remove_from_cart` fallback only. */
export const sumGa4EcommerceValue = (items) => {
  const safeItems = toGa4ItemArray(items);

  if (!safeItems.length) {
    return 0;
  }

  return roundGa4Currency(safeItems.reduce(
    (sum, item) => sum + ((Number(item.price) || 0) * (Number(item.quantity) || 1)),
    0
  )) ?? 0;
};

/** Checkout-funnel `ecommerce.value` — SCAPI `basket.productTotal`. */
export const resolveGa4BasketProductTotal = (basket) => (
  roundGa4Currency(basket?.productTotal) ?? 0
);

/** `purchase` `value` + `subTotal` — SCAPI `order.orderTotal` (full order, incl. deposit checkout). */
export const resolveGa4OrderOrderTotal = (order) => (
  roundGa4Currency(order?.orderTotal) ?? 0
);

/** `purchase` `orderId` — SCAPI `order.orderNo` with URL fallback. */
const resolveGa4OrderId = (order, orderNoFallback) => {
  const orderId = order?.orderNo || order?.orderNumber || orderNoFallback;

  return orderId ? String(orderId).trim() : '';
};

const ga4PurchaseSessionKey = (orderId) => buildSessionKey('ga4', `purchase-${orderId}`);
const ga4PaymentTxSessionKey = (orderId) => buildSessionKey('ga4', `payment-tx-${orderId}`);

const firstNonEmptyString = (...values) => {
  for (const value of values) {
    const trimmed = typeof value === 'string' ? value.trim() : '';

    if (trimmed) {
      return trimmed;
    }
  }

  return '';
};

/** Called from `triggerOrderConfirmationEmail` when gateway id is known at place-order. */
export const stashGa4PaymentTransactionId = (orderId, transactionId) => {
  if (!orderId || !transactionId) {
    return;
  }

  writeSession(ga4PaymentTxSessionKey(orderId), {
    transactionId: String(transactionId).trim(),
  });
};

/** `purchase` `ecommerce.transaction_id` — Stripe PI or PayPal capture / order id (not SFCC order no). */
const resolveGa4PaymentTransactionId = (order, orderIdFallback) => {
  const orderId = resolveGa4OrderId(order, orderIdFallback);
  const instrument = order?.paymentInstruments?.[0];

  return firstNonEmptyString(
    order?.c_stripePaymentIntentID,
    order?.c_PP_API_TransactionID,
    instrument?.paymentTransaction?.transactionId,
    instrument?.c_stripePaymentIntentID,
    instrument?.c_stripePaymentIntentId,
    instrument?.c_paypalOrderID,
    instrument?.c_externalTransactionId,
    instrument?.c_transactionRef,
    readSession(ga4PaymentTxSessionKey(orderId))?.transactionId
  );
};

export const resolveGa4OrderCoupon = (order) => {
  const couponItem = (order?.couponItems ?? []).find((entry) => entry?.code && entry.valid !== false);

  return couponItem?.code ? String(couponItem.code).trim() : '';
};

const trackGa4EcommerceEvent = async (
  eventName,
  {items, currency, customer, value, ...extraEcommerce},
  {allowEmptyItems = false, requireEcommerce = [], sumValueFallback = false} = {}
) => {
  const safeItems = toGa4ItemArray(items);

  if (!allowEmptyItems && safeItems.length === 0) {
    return false;
  }

  if (requireEcommerce.some((key) => !extraEcommerce[key])) {
    return false;
  }

  const resolvedValue = sumValueFallback && !Number.isFinite(value) ?
    sumGa4EcommerceValue(safeItems) :
    roundGa4Currency(value) ?? 0;

  return pushCustomerGa4Event(eventName, customer, {
    ecommerce: {
      currency,
      items: safeItems,
      value: resolvedValue,
      ...extraEcommerce,
    },
  });
};

export const trackAddToCart = (params) => trackGa4EcommerceEvent('add_to_cart', params, {sumValueFallback: true});

export const trackRemoveFromCart = (params) => (
  trackGa4EcommerceEvent('remove_from_cart', params, {sumValueFallback: true})
);

export const trackViewCart = (params) => trackGa4EcommerceEvent('view_cart', params, {allowEmptyItems: true});

export const trackBeginCheckout = (params) => trackGa4EcommerceEvent('begin_checkout', params);

export const trackAddPaymentInfo = (params) => (
  trackGa4EcommerceEvent('add_payment_info', params, {requireEcommerce: ['payment_type']})
);

export const trackAddShippingInfo = (params) => (
  trackGa4EcommerceEvent('add_shipping_info', params, {requireEcommerce: ['shipping_tier']})
);

export const hasGa4PurchaseBeenTracked = (orderNo) => (
  Boolean(orderNo && readSession(ga4PurchaseSessionKey(orderNo))?.tracked === true)
);

const markGa4PurchaseTracked = (orderNo) => {
  if (!orderNo) {
    return;
  }

  writeSession(ga4PurchaseSessionKey(orderNo), {tracked: true});
  clearSession(ga4PaymentTxSessionKey(orderNo));
};

/** Order confirmation — one server-side `purchase` per order (GT-011). */
export const trackPurchaseFromOrder = async ({
  api,
  currency,
  customer,
  getToken,
  order,
  orderNo: orderNoFallback,
  siteId,
  // KL-1028 — confirmation page already fetched products for line items;
  // reuse that map so `purchase` does not issue a duplicate getProducts.
  productsById: preloadedProductsById = null,
}) => {
  const orderId = resolveGa4OrderId(order, orderNoFallback);
  const paymentTransactionId = resolveGa4PaymentTransactionId(order, orderNoFallback);
  const productItems = order?.productItems ?? [];

  if (!orderId || !paymentTransactionId || !productItems.length || hasGa4PurchaseBeenTracked(orderId)) {
    return false;
  }

  const {imageMap, nameMap, productsById} = await fetchGa4OptionProductMeta({
    api,
    getToken,
    includePrices: false,
    productIds: collectGa4BasketProductIds(productItems),
    productsById: preloadedProductsById,
    useBasketProductParams: true,
  });

  const items = buildGa4BasketEventItemsFromBasket({
    basket: {productItems},
    currency,
    imageMap,
    nameMap,
    productsById,
  });

  if (!items.length) {
    return false;
  }

  const orderTotal = resolveGa4OrderOrderTotal(order);
  const coupon = resolveGa4OrderCoupon(order);
  const tracked = await pushServerSideEvent('purchase', {
    ...(await getCustomerParamsWithEmail({
      ...customer,
      email: customer?.email || order?.customerInfo?.email,
    })),
    ecommerce: {
      transaction_id: paymentTransactionId,
      affiliation: 'Online Store',
      currency: order.currency || currency,
      value: orderTotal,
      tax: roundGa4Currency(order.taxTotal) ?? 0,
      shipping: roundGa4Currency(order.shippingTotal) ?? 0,
      ...(coupon ? {coupon} : {}),
      orderId,
      subTotal: orderTotal,
      items,
    },
  }, {siteId});

  if (tracked) {
    markGa4PurchaseTracked(orderId);
  }

  return tracked;
};

const buildGa4AddToCartItemsFromSelections = (productSelectionValues, {currency} = {}) => (
  (productSelectionValues ?? [])
    .map(({product, variant, quantity}) => {
      const selectedProduct = variant || product;
      const item = toGa4AddToCartItem(selectedProduct, {
        currency,
        quantity: quantity ?? 1,
      });

      return applyGa4CartItemCategories(item, selectedProduct);
    })
    .filter(Boolean)
);

export const trackPdpAddToCartFromSelections = (params) => trackAddToCart({
  ...params,
  items: buildGa4AddToCartItemsFromSelections(params.productSelectionValues, params),
});

export const trackViewItem = async ({product, currency, customer, categoryTree}) => {
  const item = toGa4Item(product, {categoryTree, currency, includeQuantity: false});

  if (!item) {
    return false;
  }

  return pushCustomerGa4Event('view_item', customer, {
    ecommerce: {
      currency,
      items: [item],
      value: roundGa4Currency(item.price ?? 0) ?? 0,
    },
  });
};

export const trackViewItemList = async ({
  listId,
  listName,
  products,
  currency,
  customer,
}) => {
  if (!Array.isArray(products) || products.length === 0) {
    return false;
  }

  const items = products
    .map((product) => toGa4Item(product, {currency, listItem: true}))
    .filter(Boolean);

  if (!items.length) {
    return false;
  }

  return pushCustomerGa4Event('view_item_list', customer, {
    ecommerce: {item_list_id: listId, item_list_name: listName, items},
  });
};

export const useGa4ViewItemList = (listMeta, carousel) => {
  const trackerRef = useRef(null);

  if (!trackerRef.current) {
    trackerRef.current = createViewItemListTracker();
  }

  const {data: customer} = useCurrentCustomer();
  const {currency} = useCurrency();
  const swiperCleanupRef = useRef(null);
  const swiperCtxRef = useRef({});

  const trackNewProducts = useCallback(
    async (products) => {
      if (!listMeta?.id || !Array.isArray(products) || products.length === 0) {
        return false;
      }

      const unfiredProducts = trackerRef.current.filterUnfired(products);

      if (!unfiredProducts.length) {
        return false;
      }

      trackerRef.current.markFired(unfiredProducts);

      return trackViewItemList({
        currency,
        customer,
        listId: listMeta.id,
        listName: listMeta.name,
        products: unfiredProducts,
      });
    },
    [currency, customer, listMeta?.id, listMeta?.name]
  );

  const resetTracker = useCallback(() => {
    trackerRef.current.reset();
  }, []);

  if (carousel) {
    swiperCtxRef.current = {
      isOnScreen: carousel.isOnScreen,
      products: carousel.products,
      ready: carousel.ready,
      swiper: swiperCtxRef.current.swiper,
      trackNewProducts,
    };
  }

  const reportCarouselVisible = useCallback(() => {
    const {isOnScreen, products, ready, swiper, trackNewProducts: track} = swiperCtxRef.current;

    if (!ready || !isOnScreen || !swiper || !products?.length) {
      return;
    }

    swiper.updateSlidesProgress?.();

    const indices = swiper.visibleSlidesIndexes?.length ?
      swiper.visibleSlidesIndexes :
      [swiper.activeIndex ?? 0];
    const visibleProducts = indices.map((index) => products[index]).filter(Boolean);

    if (visibleProducts.length) {
      void track(visibleProducts);
    }
  }, []);

  const onSwiper = useCallback((swiper) => {
    if (!carousel) {
      return;
    }

    swiperCleanupRef.current?.();
    swiperCtxRef.current.swiper = swiper;

    const onChange = () => requestAnimationFrame(reportCarouselVisible);

    for (const event of ['slideChange', 'slideChangeTransitionEnd', 'resize']) {
      swiper.on(event, onChange);
    }

    swiperCleanupRef.current = () => {
      for (const event of ['slideChange', 'slideChangeTransitionEnd', 'resize']) {
        swiper.off(event, onChange);
      }
    };

    requestAnimationFrame(reportCarouselVisible);
  }, [carousel, reportCarouselVisible]);

  useEffect(() => {
    if (!carousel) {
      return undefined;
    }

    reportCarouselVisible();
  }, [reportCarouselVisible, carousel?.isOnScreen, carousel?.products, carousel?.ready]);

  useEffect(() => {
    return () => swiperCleanupRef.current?.();
  }, []);

  return {onSwiper, resetTracker, trackNewProducts};
};
